import { useQuery, UseQueryResult } from '@tanstack/react-query';
import siteBatchService from '../services/SiteBatchService';
import { SiteBatch, TERMINAL_BATCH_STATUSES } from '../models/SiteBatch';

const POLLING_INTERVAL_MS = 3_000;

export const siteBatchQueryKey = (projectId: string | null, batchId: number | null) =>
  ['site-batch', projectId, batchId] as const;

interface UseSiteBatchPollingOptions {
  /** Enable polling. Typically driven by "is modal open or batch active". */
  enabled: boolean;
}

/**
 * Poll the site-batch aggregate every 3 seconds while the batch is running.
 *
 * Behavior:
 * - Fetches only when `enabled` + `projectId` + `batchId` are all truthy
 * - Stops polling once the batch reaches a terminal status (completed/cancelled)
 * - Pauses while `document.hidden` (tab backgrounded) to avoid burning API calls
 * - Retries up to 3 times on transient network errors
 *
 * UI contract: the modal reads `batch.status` directly; a retry button
 * appears on failed steps only after the batch is `completed`.
 */
export function useSiteBatchPolling(
  projectId: string | null,
  batchId: number | null,
  options: UseSiteBatchPollingOptions,
): UseQueryResult<SiteBatch, Error> {
  const { enabled } = options;

  return useQuery<SiteBatch, Error>({
    queryKey: siteBatchQueryKey(projectId, batchId),
    queryFn: () => siteBatchService.getBatch(projectId as string, batchId as number),
    enabled: enabled && !!projectId && !!batchId,
    refetchInterval: (query) => {
      if (typeof document !== 'undefined' && document.hidden) {
        return false;
      }
      const status = query.state.data?.status;
      if (!status) return POLLING_INTERVAL_MS;
      return TERMINAL_BATCH_STATUSES.includes(status) ? false : POLLING_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
    retry: 3,
  });
}
